<template>
  <div v-if="masteryStore.loader.fetchAssessmentMasteryData === true" class="flex items-center justify-center py-8">
    <spinner-loader :color="'text-blue-600 dark:text-blue-600'" />
  </div>

  <div v-else-if="hasData">
    <v-chart :loading="masteryStore.loader.fetchAssessmentMasteryData" class="h-[20rem] chart" :option="option"
      :theme="rlTheme.theme" :autoresize="true" @click="handleClick" ref="currentMasteryChart" />
  </div>

  <div v-else class="flex flex-col items-center justify-center border-2 border-dashed border-[#EAECF0] rounded-xl py-12 px-4 text-center min-h-[20rem]">
    <p class="text-sm text-gray-500 font-medium">
      {{ $t("concepts_view.no_data") }}
    </p>
  </div>
</template>

<script setup>
import VChart from "vue-echarts";
import { ref, watch, onMounted, inject, computed } from "vue";
import i18n from "@/plugins/i18n";
import rlTheme from "@/assets/echarts/rl-theme.json";
import { useMasteryStore } from "@/stores/masteryStore";
import { useLearnerStore } from "@/stores/learnerStore";
import { useFilterStore } from "@/stores/filterStore";

const masteryStore = useMasteryStore();
const learnerStore = useLearnerStore();
const filterStore = useFilterStore();

const bus = inject("bus");

// Get thread prop
const props = defineProps({
  thread: {
    type: Object,
    required: true,
  },
});

// const thread = ref(props.thread);
const threadData = ref([
  {
    users: [],
  },
]);

const option = ref({});

const hasData = computed(() => {
  return threadData.value && threadData.value.length > 0 && threadData.value[0]?.users?.length > 0;
});

watch(
  () => masteryStore.loader.fetchAssessmentMasteryData,
  (fetchAssessmentMasteryData) => {
    if (fetchAssessmentMasteryData === false) {
      filterData(props.thread);
    }
  }
);

// Watch thread prop
watch(
  () => props.thread,
  (thread) => {
    filterData(thread);
  }
);

// Watch assessmentMasteryData directly to ensure we react on store changes
watch(
  () => masteryStore.assessmentMasteryData,
  () => {
    filterData(props.thread);
  },
  { deep: true }
);

onMounted(() => {
  if (props.thread) {
    filterData(props.thread);
  }
});

const handleClick = (params) => {
  if (params.componentType === "series") {
    bus.emit("show-student-modal", { userId: params.data.userId });
  }
};

function processThreadData(threadData) {
  const latestResults = threadData.value[0].users.filter(
    (element) => element.latestResults !== null
  );

  // Fallback check for baselineResults
  const baselineResults = threadData.value[0].users.filter(
    (element) => element.latestResults === null && element.baselineResults !== null
  );

  // Combine the levels with fallback
  latestResults.push(...baselineResults);

  const incomplete = threadData.value[0].users.filter(
    (element) => element.baselineResults === null && element.latestResults === null
  );

  // Sort the levels
  latestResults.sort((a, b) => Math.max(a.latestResults, a.baselineResults) - Math.max(b.latestResults, b.baselineResults) || a.baselineResults - b.baselineResults);

  // Combine the levels
  return [
    ...incomplete,
    ...latestResults,
  ];
}

function getLevelMultiplierByGrade(grade) {
  if (grade >= 1 && grade <= 4) return 1;
  if (grade >= 5 && grade <= 7) return 2;
  if (grade >= 8 && grade <= 13) return 3;
  return 1;
}

const filterData = (thread) => {
  if (!thread) {
    threadData.value = [];
    option.value = {};
    return;
  }

  // Filter data
  const rawData = masteryStore?.getAssessmentMasteryData;
  threadData.value = Array.isArray(rawData)
    ? rawData.filter((item) => item.threadId === thread.threadId)
    : [];

  if (!threadData.value || threadData.value.length === 0 || !threadData.value[0]?.users || threadData.value[0].users.length === 0) {
    option.value = {};
    return;
  }

  const level3Learners = [];
  const level6Learners = [];
  const level9Learners = [];

  if (threadData.value && threadData.value.length > 0) {
    threadData.value[0].users.forEach((element) => {
      const expectedLevel = masteryStore.expectedLevelByLearnerId(element.userId);
      const currentLearnerAssessment = learnerStore.getLearnerAssessmentBySelectedSubject(element.userId);
      element.assessmentType = currentLearnerAssessment?.type?.replaceAll('_', " ");
      if (expectedLevel === 3) {
        level3Learners.push(element);
      } else if (expectedLevel === 6) {
        level6Learners.push(element);
      } else if (expectedLevel === 9) {
        level9Learners.push(element);
      }
    });

    let level3LearnersData = [];
    let level6LearnersData = [];
    let level9LearnersData = [];
    let maxLevel = 0;

    if (level3Learners.length > 0) {
      level3LearnersData = processThreadData({
        value: [
          {
            users: level3Learners,
          },
        ],
      });

      maxLevel = 3;
    }

    if (level6Learners.length > 0) {
      level6LearnersData = processThreadData({
        value: [
          {
            users: level6Learners,
          },
        ],
      });

      maxLevel = 6;
    }

    if (level9Learners.length > 0) {
      level9LearnersData = processThreadData({
        value: [
          {
            users: level9Learners,
          },
        ],
      });

      maxLevel = 9;
    }

    // Combine the data
    const threadsData = [
      ...level3LearnersData,
      ...level6LearnersData,
      ...level9LearnersData,
    ];

    // if (threadData.value && threadData.value.length > 0) {
    // const threadsData = processThreadData(threadData);

    threadsData.forEach((element) => {
      element.expectedLevel = masteryStore.expectedLevelByLearnerId(element.userId);
    });

    const levelMultiplier = getLevelMultiplierByGrade(
      filterStore.selectedFilters.gradeLevel
    );

    option.value = {
      legend: {
        data: [
          i18n.global.t("overview_modal.incomplete"),
          i18n.global.t("overview_modal.baseline"),
          i18n.global.t("overview_modal.latest"),
        ],
        textStyle: {
          fontSize: 14, // Adjust font size here
          color: "#0D1828"
        },
        itemWidth: 8, // Adjust the box size (width)
        itemHeight: 8, // Adjust the box size (height)
        icon: "circle", // Change the legend icon to a circle shape
      },
      xAxis: {
        data: threadsData.map((element) => element.userFullName + '|' + element.assessmentType),
        name: i18n.global.t("overview_modal.individual_students"),
        nameLocation: "middle",
        nameGap: 20,
        nameTextStyle: {  
          fontSize: 14,
          color: "#0D1828",
        },
        axisLabel: {
          formatter: "",
        },
        axisTick: {
          length: 0,
        },
        axisLine: {},
      },
      yAxis: {
        type: "value",
        name: i18n.global.t("overview_modal.grade_level_mastered"),
        min: 0,
        max: maxLevel,
        splitNumber: 3,
        nameLocation: "middle",
        nameGap: 30,
        nameTextStyle: {
          fontSize: 14,
          color: "#0D1828"
        },
        // axisLabel: {
        //   formatter: (value) => Math.floor(value),
        // },
        splitLine: {},
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: function (params) {
          let tooltipContent = params[0].name.split('|')[0] + '<br>';
          params.forEach(param => {
            tooltipContent += `${param.marker}${param.seriesName}: <span style="float: right; font-weight: bold;">${param.value != null ? param.value : '-'}</span><br>`;
          });
          tooltipContent += '<span style="font-size:12px">' + params[0].name.split('|')[1] + '</span>';
          return tooltipContent;
        }
      },
      series: [
        // incomplete
        {
          name: i18n.global.t("overview_modal.incomplete"),
          data: threadsData.map((element) => {
            if (element.baselineResults == null) {
              return { value: 0.5, userId: element.userId };
            }
          }),
          type: "bar",
          barMaxWidth: 15,
          stack: "x",
          z: 6,
          tooltip: {
            show: false
          }
        },
        // Baseline
        {
          name: i18n.global.t("overview_modal.baseline"),
          data: threadsData.map((element) => {
            if (element.baselineResults >= 0) {
              return { value: element.baselineResults, userId: element.userId };
            }
          }),
          type: "bar",
          barMaxWidth: 15,
          stack: "x",
          z: 5
        },
        // Latest
        {
          name: i18n.global.t("overview_modal.latest"),
          data: threadsData.map((element) => {
            if (element.latestResults > element.baselineResults) {
              return {
                value: element.latestResults - element.baselineResults,
                userId: element.userId,
              };
            }
          }),
          type: "bar",
          barMaxWidth: 15,
          stack: "x",
          z: 1,
          markLine: {
            data: threadsData.map((element) => {
              return {
                name: i18n.global.t("overview_modal.expected_level"),
                yAxis: 3 * levelMultiplier,
                xAxis: element.userId,
              }
            }).filter(Boolean),
            lineStyle: {
              color: 'green',
              type: 'solid',
            },
            symbol: ['none', 'none'],
            label: {
              formatter: i18n.global.t("overview_modal.expected_level"),
            },
          }
        },
      ],
    };
  }
};
</script>

